/**
 * Blog post format: a markdown file with a small frontmatter block.
 *
 * This module is pure (no Vite, no DOM) on purpose. The browser uses it to render
 * a post, and scripts/prerender.mjs uses the very same functions to write the
 * post's HTML and JSON-LD at build time. So the article Google crawls, the one a
 * visitor reads, and the FAQ schema can never drift apart. The FAQPage schema is
 * built from the same strings as the visible FAQ, which Google requires.
 *
 * Only the markdown subset the posts use is supported: ## / ### headings,
 * paragraphs, - and 1. lists, **bold**, [links](url), > quotes and pipe tables.
 * Anything else renders as plain text. That is deliberate: it keeps the output
 * predictable and escaped.
 */

export interface BlogFaq {
  q: string;
  a: string;
}

export interface BlogPostData {
  slug: string;
  title: string;
  /** Used verbatim as the HTML <title>. Keep it to 60 characters or fewer. */
  metaTitle: string;
  description: string;
  date: string;
  updated: string;
  author: string;
  category: string;
  takeaways: string[];
  faq: BlogFaq[];
  /** Markdown source of the article body. */
  body: string;
}

export const SITE_ORIGIN = 'https://www.omnicraftservices.com';

/** Real people only. A post whose author is not listed here fails the build. */
export const AUTHORS: Record<string, { name: string; role: string; url: string }> = {
  Zain: { name: 'Zain', role: 'Technical Lead, Omnicraft Services', url: `${SITE_ORIGIN}/about` },
};

const REQUIRED: (keyof BlogPostData)[] = [
  'slug', 'title', 'metaTitle', 'description', 'date', 'updated', 'author', 'category',
];

/** Parse one post file. Throws with the file's slug on any format error. */
export function parsePost(source: string, fileName = '?'): BlogPostData {
  const m = source.replace(/\r\n/g, '\n').match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!m) throw new Error(`blog ${fileName}: missing --- frontmatter block`);
  const [, head, body] = m;

  const data: Record<string, unknown> = { takeaways: [], faq: [] };
  let list: 'takeaways' | 'faq' | null = null;

  for (const raw of head.split('\n')) {
    if (!raw.trim()) continue;
    const item = raw.match(/^-\s+(.*)$/);
    const answer = raw.match(/^\s+a:\s*(.*)$/);
    const key = raw.match(/^(\w+):\s*(.*)$/);

    if (list === 'faq' && item) {
      const q = item[1].match(/^q:\s*(.*)$/);
      if (!q) throw new Error(`blog ${fileName}: faq item must start with "- q:"`);
      (data.faq as BlogFaq[]).push({ q: q[1].trim(), a: '' });
    } else if (list === 'faq' && answer) {
      const faq = data.faq as BlogFaq[];
      if (!faq.length) throw new Error(`blog ${fileName}: "a:" before any "q:"`);
      faq[faq.length - 1].a = answer[1].trim();
    } else if (list === 'takeaways' && item) {
      (data.takeaways as string[]).push(item[1].trim());
    } else if (key) {
      const [, k, v] = key;
      if (k === 'takeaways' || k === 'faq') {
        list = k;
      } else {
        list = null;
        data[k] = v.trim();
      }
    } else {
      throw new Error(`blog ${fileName}: cannot parse frontmatter line: ${raw}`);
    }
  }

  for (const k of REQUIRED) {
    if (!data[k]) throw new Error(`blog ${fileName}: missing "${k}"`);
  }
  const post = { ...data, body: body.trim() } as BlogPostData;
  if (!AUTHORS[post.author]) throw new Error(`blog ${fileName}: unknown author "${post.author}"`);
  if (post.faq.some((f) => !f.a)) throw new Error(`blog ${fileName}: a faq question has no answer`);
  return post;
}

/* ------------------------------------------------------------ rendering -- */

export const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Inline markdown: **bold** and [text](url). Input is escaped first. */
function inline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label: string, href: string) => {
      const external = /^https?:\/\//.test(href) && !href.startsWith(SITE_ORIGIN);
      return external
        ? `<a href="${href}" target="_blank" rel="noopener">${label}</a>`
        : `<a href="${href}">${label}</a>`;
    });
}

/** Stable id for a heading, so sections can be linked to. */
export const headingId = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD') // "después" -> "despues", not "despu-s"
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\*\*/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Render the post body to HTML. */
export function renderMarkdown(md: string): string {
  const out: string[] = [];
  // A heading is always its own block, even when no blank line follows it.
  const blocks = md.replace(/^(#{2,3}\s.*)$/gm, '\n$1\n').split(/\n{2,}/);

  for (const block of blocks) {
    const lines = block.split('\n').map((l) => l.trimEnd()).filter(Boolean);
    if (!lines.length) continue;
    const first = lines[0];

    let h;
    if ((h = first.match(/^(#{2,3})\s+(.*)$/)) && lines.length === 1) {
      const level = h[1].length;
      out.push(`<h${level} id="${headingId(h[2])}">${inline(h[2])}</h${level}>`);
    } else if (lines.every((l) => /^-\s+/.test(l))) {
      out.push(`<ul>${lines.map((l) => `<li>${inline(l.replace(/^-\s+/, ''))}</li>`).join('')}</ul>`);
    } else if (lines.every((l) => /^\d+\.\s+/.test(l))) {
      out.push(`<ol>${lines.map((l) => `<li>${inline(l.replace(/^\d+\.\s+/, ''))}</li>`).join('')}</ol>`);
    } else if (lines.every((l) => l.startsWith('>'))) {
      out.push(`<blockquote>${lines.map((l) => inline(l.replace(/^>\s?/, ''))).join(' ')}</blockquote>`);
    } else if (lines.length >= 2 && lines.every((l) => l.startsWith('|')) && /^\|[\s:|-]+\|$/.test(lines[1])) {
      const cells = (l: string) => l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(lines[0]).map((c) => `<th>${inline(c)}</th>`).join('');
      const rows = lines
        .slice(2)
        .map((l) => `<tr>${cells(l).map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`)
        .join('');
      out.push(`<div class="table-wrap"><table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`);
    } else {
      out.push(`<p>${inline(lines.join(' '))}</p>`);
    }
  }
  return out.join('\n');
}

/** Rough reading time at 230 words per minute. */
export const readingMinutes = (post: BlogPostData) =>
  Math.max(1, Math.round(post.body.split(/\s+/).length / 230));

/* ---------------------------------------------------------- structured data -- */

export const postUrl = (post: BlogPostData) => `${SITE_ORIGIN}/blog/${post.slug}`;

/** BlogPosting + BreadcrumbList + FAQPage, all derived from the visible content. */
export function postJsonLd(post: BlogPostData) {
  const url = postUrl(post);
  const author = AUTHORS[post.author];
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'BlogPosting',
      '@id': `${url}#article`,
      headline: post.title,
      description: post.description,
      url,
      mainEntityOfPage: url,
      datePublished: post.date,
      dateModified: post.updated,
      articleSection: post.category,
      inLanguage: 'en-US',
      author: { '@type': 'Person', name: author.name, jobTitle: author.role, url: author.url },
      publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_ORIGIN}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];
  if (post.faq.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: post.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

/**
 * The complete article as static HTML: the markup the prerenderer puts inside
 * #root, and the same markup React renders. One function, so the two cannot
 * disagree.
 */
export function renderArticle(post: BlogPostData): string {
  const author = AUTHORS[post.author];
  const date = new Date(`${post.date}T12:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
  const takeaways = post.takeaways.length
    ? `<aside class="takeaways" aria-label="Key takeaways"><h2>Key takeaways</h2><ul>${post.takeaways
        .map((t) => `<li>${inline(t)}</li>`)
        .join('')}</ul></aside>`
    : '';
  const faq = post.faq.length
    ? `<section class="faq" aria-labelledby="faq"><h2 id="faq">Frequently asked questions</h2>${post.faq
        .map((f) => `<h3>${escapeHtml(f.q)}</h3><p>${escapeHtml(f.a)}</p>`)
        .join('')}</section>`
    : '';
  return [
    `<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Home</a> / <a href="/blog">Blog</a> / <span>${escapeHtml(post.category)}</span></nav>`,
    `<h1>${escapeHtml(post.title)}</h1>`,
    `<p class="byline">By <a href="/about">${escapeHtml(author.name)}</a>, ${escapeHtml(author.role)} · <time datetime="${post.date}">${date}</time> · ${readingMinutes(post)} min read</p>`,
    takeaways,
    renderMarkdown(post.body),
    faq,
  ].join('\n');
}
